import {
  PointerEvent,
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { Tuple } from "_/types";

import { SliderSize } from "./styled";
import * as S from "./styled";

import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
  type Placement as FUPlacement,
} from "@floating-ui/react";

type SliderProps<T extends number | Tuple<number>> = {
  /**
   * Minimum value of the slider.
   */
  min?: number;
  /**
   * Maximum value of the slider.
   */
  max?: number;
  /**
   * Values of the slider thumbs.
   * The number of values determines the number of thumbs.
   */
  value: T;
  /**
   * The amount to increment/decrement the slider value when the thumb is moved.
   */
  step: number;
  /**
   * Callback function that is called when the slider value changes.
   */
  onChange?: (value: T) => void;
  /**
   * Optional text to show next to a slider thumb when hovered or dragged.
   */
  tooltipText?: (value: T) => string[];
  /**
   * If a thumb tooltip is shown, where should it be shown?
   *
   * `"before"` will show the tooltip on top of a thumb in horizontal
   * orientation and to the left of a thumb in vertical orientation. `"after"`
   * will show the tooltip below the thumb in horizontal orientation and to the
   * right of the thumb in vertical orientation.
   *
   * Ignored if `tooltipText` is `undefined`.
   */
  tooltipPlacement?: "before" | "after";
  /**
   * The size of the slider.
   */
  size?: SliderSize;
  /**
   * True if the slider should be oriented vertically.
   */
  vertical?: boolean;

  /** Optional CSS backgrounds/colors used by composed slider controls. */
  trackBackground?: string;
  rangeBackground?: string;
  thumbColor?: string;
  /** Optional narrow painted thumb inside the full-size grab target. */
  thumbVisualWidth?: number;
};

export const Slider = <T extends number | Tuple<number>>({
  min,
  max,
  value,
  step,
  onChange,
  tooltipText,
  tooltipPlacement = "before",
  size = "small",
  vertical = false,
  trackBackground,
  rangeBackground,
  thumbColor,
  thumbVisualWidth,
}: SliderProps<T>): ReactElement => {
  // Normalize scalar (single thumb) and tuple (dual thumb) values into an array
  const valueCount = typeof value === "number" ? 1 : value.length;
  const values: number[] = typeof value === "number" ? [value] : value;
  const [committedValues, setCommittedValues] = useState(values);

  // The "active thumb" is the one that is currently being dragged.
  // This is needed because our slider component frustratingly sorts
  // thumb positions while dragging; there's no persistent concept of
  // a first thumb and a second thumb. We need to know which thumb is
  // currently being dragged so we can prevent the user from dragging
  // one thumb to the other side of the other thumb.
  const [activeThumbIndex, setActiveThumbIndex] = useState<0 | 1 | null>(null);

  // Track which thumb is hovered or dragged (for tooltip visibility)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const openIndex = dragIndex ?? hoverIndex;
  const clearHover = () => setHoverIndex(null);
  const clearDrag = () => setDragIndex(null);

  const thumbRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const setThumbRef = (i: number) => (el: HTMLSpanElement | null) => {
    thumbRefs.current[i] = el;
  };

  const placement: FUPlacement = useMemo(() => {
    if (vertical) {
      return tooltipPlacement === "before" ? "left-start" : "right-start";
    }
    return tooltipPlacement === "before" ? "top" : "bottom-start";
  }, [vertical, tooltipPlacement]);

  const { refs, floatingStyles, update } = useFloating({
    placement,
    strategy: "fixed",
    // Use animation frame while mounted so the tooltip tracks smoothly during drag
    whileElementsMounted: (refEl, floatEl, updateFn) =>
      autoUpdate(refEl, floatEl, updateFn, { animationFrame: true }),
    middleware: [
      offset(8),
      flip(),
      shift(),
      offset({ mainAxis: 2, crossAxis: -8 }),
    ],
  });

  // Fix FOUC (tooltip appearing briefly at 0,0) by computing position pre-paint
  const [isPositioned, setIsPositioned] = useState(false);

  useLayoutEffect(() => {
    const el = openIndex != null ? thumbRefs.current[openIndex] : null;
    if (!el) {
      setIsPositioned(false);
      return;
    }
    refs.setReference(el);
    update?.(); // one initial sync before first paint
    setIsPositioned(true);
  }, [openIndex, refs, update]);

  // new: clear drag on any pointer up / cancel / window blur while dragging
  useLayoutEffect(() => {
    if (dragIndex == null) return undefined;

    const clear = () => setDragIndex(null);
    window.addEventListener("pointerup", clear, true);
    window.addEventListener("pointercancel", clear, true);
    window.addEventListener("blur", clear);

    return () => {
      window.removeEventListener("pointerup", clear, true);
      window.removeEventListener("pointercancel", clear, true);
      window.removeEventListener("blur", clear);
    };
  }, [dragIndex]);

  const onThumbPointerDown = (i: number) => (e: PointerEvent) => {
    // Cast OK because we're testing for presence of `setPointerCapture`.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    (e.currentTarget as HTMLElement)?.setPointerCapture?.(e.pointerId);
    setDragIndex(i);
  };

  const labels = tooltipText?.(value) ?? [];

  const onValueChange = useCallback(
    (newVals: number[]) => {
      const newValues = [...newVals]; // Clone in case mutation is bad here.
      if (valueCount === 2) {
        const differentFromCommitted = newValues.map(
          (v, i) => v !== committedValues[i]
        );
        const adjustValues = (thumbIndex: number) => {
          // Prevent thumbs from crossing each other while dragging
          if (thumbIndex === 0) {
            newValues[0] = Math.min(newValues[0], committedValues[1]);
            newValues[1] = committedValues[1];
          } else {
            newValues[1] = Math.max(newValues[1], committedValues[0]);
            newValues[0] = committedValues[0];
          }
        };

        if (activeThumbIndex !== null) {
          adjustValues(activeThumbIndex);
        } else if (
          // If the last committed values are the same, then allow the user to
          // move the thumb anywhere.
          committedValues[0] !== committedValues[1]
        ) {
          if (differentFromCommitted[0] && differentFromCommitted[1]) {
            // If neither thumb is in the same place as the committed values,
            // then the user is trying to drag one thumb to the other side of
            // the other thumb. This might not be possible given the code below
            // but it can't hurt to prevent it just in case.
            console.warn("Unexpected change of both slider thumbs.");
            return;
          } else if (differentFromCommitted[0]) {
            setActiveThumbIndex(0);
          } else if (differentFromCommitted[1]) {
            setActiveThumbIndex(1);
          }
        }
      }

      // TS doesn't know that if there's only one thumb then T is a scalar, but
      // if there are thumbs then it's a tuple.
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      const valueForHandler = (
        valueCount === 1 ? newValues[0] : newValues
      ) as T;
      onChange?.(valueForHandler);
    },
    [activeThumbIndex, committedValues, onChange, valueCount]
  );

  return (
    <S.SliderRoot
      min={min}
      max={max}
      value={values}
      step={step}
      onValueCommit={(newVals: Tuple<number>) => {
        setCommittedValues(newVals);
        setActiveThumbIndex(null);
        setDragIndex(null);
      }}
      onPointerDown={() => {
        setCommittedValues(values);
        setActiveThumbIndex(null);
      }}
      onValueChange={onValueChange}
      $size={size}
      orientation={vertical ? "vertical" : "horizontal"}
      onMouseLeave={clearHover}
    >
      <S.SliderTrack $size={size} $background={trackBackground}>
        <S.SliderRange $background={rangeBackground} />
      </S.SliderTrack>

      {values.map((_, i) => (
        <S.SliderThumb
          key={i}
          ref={setThumbRef(i)}
          $size={size}
          $color={thumbColor}
          $visualWidth={thumbVisualWidth}
          onMouseEnter={() => setHoverIndex(i)}
          onMouseLeave={clearHover}
          onPointerDown={onThumbPointerDown(i)}
          onPointerUp={clearDrag}
        />
      ))}

      {/* Inline tooltip rendered outside the scroller via portal */}
      {tooltipText && openIndex != null && thumbRefs.current[openIndex] && (
        <FloatingPortal>
          <S.Tooltip
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              // Hide until first computed position to prevent flash
              visibility: isPositioned ? undefined : "hidden",
            }}
            role="tooltip"
          >
            {labels[openIndex]}
          </S.Tooltip>
        </FloatingPortal>
      )}
    </S.SliderRoot>
  );
};
